import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { useSeo } from '@/hooks/useSeo';
import { articles, relatedArticles } from '@/mocks/chigua';

export default function Post() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);
  const related = relatedArticles(id || '', 3);

  useSeo(
    article
      ? {
          title: `${article.title} · ${article.categoryLabel}`,
          description: article.desc,
          keywords: `吃瓜路线,${article.tags.join(',')},围观指南`,
        }
      : { title: '内容不存在', description: '你要找的内容不存在，可能已下线或链接有误。' },
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="container-x py-20 text-center">
          <i className="ri-emotion-sad-line text-5xl text-secondary-300" aria-hidden="true"></i>
          <h1 className="mt-4 font-heading text-2xl text-foreground-900">这篇瓜没能找到</h1>
          <p className="mx-auto mt-3 max-w-md text-sm text-foreground-500">内容可能已下线或链接有误，回到热榜看看其他话题吧。</p>
          <Link
            to="/hot"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            返回今日热榜
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.desc,
    datePublished: article.date,
    author: { '@type': 'Person', name: article.author },
    publisher: { '@type': 'Organization', name: '吃瓜路线' },
    image: article.image,
    articleSection: article.categoryLabel,
    keywords: article.tags.join(','),
    mainEntityOfPage: { '@type': 'WebPage', '@id': typeof window !== 'undefined' ? window.location.href : undefined },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: `${typeof window !== 'undefined' ? window.location.origin : ''}/` },
      { '@type': 'ListItem', position: 2, name: '今日瓜点热榜', item: `${typeof window !== 'undefined' ? window.location.origin : ''}/hot` },
      { '@type': 'ListItem', position: 3, name: article.categoryLabel },
    ],
  };

  return (
    <>
      <JsonLd data={articleLd} />
      <JsonLd data={breadcrumbLd} />
      <Navbar />
      <main>
        <article className="container-x max-w-3xl py-10 md:py-14">
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <Link to="/hot" className="hover:text-primary-600">今日热榜</Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <Link to={`/hot?cat=${article.category}`} className="hover:text-primary-600">{article.categoryLabel}</Link>
          </nav>

          <header className="mt-6">
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-md bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-800">{article.categoryLabel}</span>
              {article.hot === '今日最热' && (
                <span className="inline-flex items-center gap-1 rounded-md bg-accent-500 px-2.5 py-1 text-xs font-medium text-background-50">
                  <i className="ri-fire-line" aria-hidden="true"></i>
                  今日最热
                </span>
              )}
              <span className="text-xs text-foreground-400">{article.date}</span>
            </div>
            <h1 className="mt-4 font-heading text-2xl leading-snug text-foreground-900 md:text-4xl md:leading-tight">
              {article.title}
            </h1>
            <p className="mt-4 text-base leading-7 text-foreground-600">{article.desc}</p>
            <div className="mt-6 flex items-center justify-between border-y border-background-200 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 font-heading text-base text-background-50">
                  {article.author.slice(0, 1)}
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                  <p className="text-xs text-foreground-500">{article.authorRole}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-foreground-500">
                <span className="inline-flex items-center gap-1">
                  <i className="ri-eye-line" aria-hidden="true"></i>
                  {article.views} 阅读
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-time-line" aria-hidden="true"></i>
                  约 {article.readTime} 分钟
                </span>
              </div>
            </div>
          </header>

          <figure className="mt-8 overflow-hidden rounded-3xl">
            <div className="aspect-[3/1] w-full md:aspect-[16/7]">
              <img src={article.image} alt={article.title} title={`${article.title} - 吃瓜路线`} className="h-full w-full object-cover" />
            </div>
          </figure>

          <div className="mt-8 space-y-6 text-[15px] leading-8 text-foreground-700">
            {article.body.map((p, i) => (
              <p key={i} className={i === 0 ? 'first-letter:float-left first-letter:mr-2 first-letter:font-heading first-letter:text-5xl first-letter:leading-none first-letter:text-primary-500' : ''}>
                {p}
              </p>
            ))}
          </div>

          <footer className="mt-10 border-t border-background-200 pt-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/hot?kw=${encodeURIComponent(tag)}`}
                  className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-800 transition-colors hover:bg-primary-100 hover:text-primary-700"
                >
                  #{tag}
                </Link>
              ))}
            </div>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/hot"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600"
              >
                <i className="ri-arrow-left-line" aria-hidden="true"></i>
                回到今日热榜
              </Link>
              <Link
                to="/#subscribe"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-300 px-6 py-3 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-mail-add-line" aria-hidden="true"></i>
                订阅每日瓜讯
              </Link>
            </div>
          </footer>
        </article>

        {/* 相关推荐 */}
        {related.length > 0 && (
          <section className="border-t border-background-200 bg-background-100 py-12 md:py-14">
            <div className="container-x">
              <div className="flex items-end justify-between">
                <h2 className="font-heading text-2xl text-foreground-900">你可能还想围观</h2>
                <Link to="/hot" className="hidden items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 sm:inline-flex">
                  查看更多 <i className="ri-arrow-right-line text-xs" aria-hidden="true"></i>
                </Link>
              </div>
              <div className="mt-7 grid grid-cols-1 gap-5 md:grid-cols-3">
                {related.map((r) => (
                  <article key={r.id} className="group overflow-hidden rounded-3xl border border-background-200 bg-background-50 transition-all hover:-translate-y-1 hover:border-primary-200">
                    <Link to={`/post/${r.id}`} className="block overflow-hidden" aria-label={r.title}>
                      <div className="aspect-[3/2] w-full overflow-hidden">
                        <img src={r.image} alt={r.title} title={`${r.title} - 吃瓜路线`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      </div>
                    </Link>
                    <div className="p-5">
                      <span className="rounded-md bg-accent-100 px-2 py-0.5 text-xs text-accent-800">{r.categoryLabel}</span>
                      <h3 className="mt-2.5 text-base font-medium leading-snug text-foreground-900">
                        <Link to={`/post/${r.id}`} className="transition-colors hover:text-primary-600">
                          {r.title}
                        </Link>
                      </h3>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}